import { Button, ResponsiveTable, useTranslation } from '@hbs-us/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import type { PayrollResponse } from './contractsMirror.js';
import { RunPayrollConfirm } from './RunPayrollConfirm.js';
import type { PayrollSearch } from './searchParams.js';
import { useRunPayrollMutation } from './useRunPayrollMutation.js';

/**
 * The faithful Payroll Center port (UI-SPEC Payroll row) on the locked Velocity brand — the most
 * sensitive, money-committing screen. Page wrapper + header + the pay-period prev/next selector + the
 * per-employee computed-gross REVIEW GRID + the "Run Payroll" CTA (gated behind an explicit confirm
 * showing the total + count).
 *
 * The coarse endpoint (22-14) owns the gross-pay computation via the golden-locked payroll-calc.ts.
 * This view RENDERS the server-computed grossPay verbatim — it performs NO OT / lunch / ÷52 math
 * (D-03 / T-22-payroll). Money numerals are font-mono with AA contrast.
 *
 * Run Payroll is pessimistic: the confirm shows the total before commit, the mutation has NO optimistic
 * UI, and the committed locked run is reflected only after the server responds (then we navigate to the
 * Run Detail).
 */

/** Add `days` days to a Monday ISO week-start (UTC-stable), returning the new ISO date. */
function shiftWeek(weekStart: string, days: number): string {
  const d = new Date(`${weekStart}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export interface PayrollCenterViewProps {
  data: PayrollResponse;
  search: PayrollSearch;
}

export function PayrollCenterView({ data }: PayrollCenterViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatCurrency } = useMoneyFormatters();
  const runPayroll = useRunPayrollMutation();

  const weekStart = data.weekStart ?? '';
  const weekEnd = data.weekEnd ?? '';
  const entries = data.entries ?? [];
  const totalGrossPay = Number(data.totalGrossPay ?? 0);
  const employeeCount = Number(data.employeeCount ?? entries.length);
  const existingRun = data.existingRun ?? null;

  const [confirmOpen, setConfirmOpen] = useState(false);

  const prevWeek = weekStart ? shiftWeek(weekStart, -7) : undefined;
  const nextWeek = weekStart ? shiftWeek(weekStart, 7) : undefined;

  const onConfirmRun = () => {
    if (!weekStart) return;
    // NO optimistic UI — the run is committed ONLY by the server. On success we navigate to the
    // locked Run Detail rendered from the server-returned snapshot.
    runPayroll.mutate(
      { weekStart },
      {
        onSuccess: (res) => {
          setConfirmOpen(false);
          if (res.ok && res.run) {
            void navigate({ to: '/payroll/runs/$id', params: { id: String(res.run.id) } });
          }
        },
      },
    );
  };

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {t('payroll:title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">{t('payroll:subtitle')}</p>
        </div>
        <Button asChild variant="secondary">
          <Link to="/payroll/history">{t('payroll:actions.history')}</Link>
        </Button>
      </header>

      <section
        aria-label={t('payroll:summary.regionLabel')}
        className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
      >
        <SummaryCard
          label={t('payroll:summary.totalGross')}
          value={formatCurrency(totalGrossPay)}
          mono
        />
        <SummaryCard label={t('payroll:summary.employees')} value={String(employeeCount)} />
      </section>

      <nav
        aria-label={t('payroll:nav.regionLabel')}
        className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card p-4"
      >
        <div className="flex items-center gap-2">
          {prevWeek ? (
            <Button asChild variant="secondary" size="sm">
              <Link to="/payroll" search={{ week: prevWeek }}>
                {t('payroll:nav.prevWeek')}
              </Link>
            </Button>
          ) : null}
          {nextWeek ? (
            <Button asChild variant="secondary" size="sm">
              <Link to="/payroll" search={{ week: nextWeek }}>
                {t('payroll:nav.nextWeek')}
              </Link>
            </Button>
          ) : null}
        </div>
        <p className="font-mono text-muted-foreground text-sm">
          {weekStart} – {weekEnd}
        </p>
      </nav>

      {existingRun ? (
        <div
          role="status"
          className="mb-4 rounded-md border border-border bg-secondary/40 p-4 text-sm"
        >
          <p className="text-foreground">{t('payroll:lockedNotice.body')}</p>
          <Link
            to="/payroll/runs/$id"
            params={{ id: String(existingRun.id) }}
            className="mt-1 inline-block font-medium text-primary-ink underline underline-offset-2"
          >
            {t('payroll:lockedNotice.viewRun')}
          </Link>
        </div>
      ) : null}

      {entries.length > 0 ? (
        <ResponsiveTable className="mb-6 rounded-md border border-border bg-card">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">{t('payroll:grid.caption')}</caption>
            <thead>
              <tr className="border-border border-b text-left text-muted-foreground">
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('payroll:grid.employee')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('payroll:grid.payType')}
                </th>
                <th scope="col" className="px-4 py-3 text-right font-medium">
                  {t('payroll:grid.regularHours')}
                </th>
                <th scope="col" className="px-4 py-3 text-right font-medium">
                  {t('payroll:grid.overtimeHours')}
                </th>
                <th scope="col" className="px-4 py-3 text-right font-medium">
                  {t('payroll:grid.grossPay')}
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.employeeId} className="border-border/60 border-b last:border-b-0">
                  <td className="px-4 py-3 font-medium text-foreground">{e.employeeName}</td>
                  <td className="px-4 py-3 text-muted-foreground">{e.payType}</td>
                  <td className="px-4 py-3 text-right font-mono text-foreground">
                    {e.payType === 'Hourly' ? e.regularHours.toFixed(2) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-foreground">
                    {e.payType === 'Hourly' ? e.overtimeHours.toFixed(2) : '—'}
                  </td>
                  {/* The golden server gross-pay, rendered verbatim (no client recompute). */}
                  <td className="px-4 py-3 text-right font-mono font-semibold text-foreground">
                    {formatCurrency(e.grossPay)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-border border-t">
                <td className="px-4 py-3 font-semibold text-foreground" colSpan={4}>
                  {t('payroll:grid.total')}
                </td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-foreground">
                  {formatCurrency(totalGrossPay)}
                </td>
              </tr>
            </tfoot>
          </table>
        </ResponsiveTable>
      ) : (
        <div className="mb-6 rounded-md border border-border bg-card p-16 text-center">
          <h2 className="font-display font-semibold text-foreground text-xl">
            {t('payroll:empty.heading')}
          </h2>
          <p className="mx-auto mt-2 max-w-sm text-muted-foreground text-sm">
            {t('payroll:empty.body')}
          </p>
        </div>
      )}

      {entries.length > 0 && !existingRun ? (
        <div className="flex justify-end">
          <Button onClick={() => setConfirmOpen(true)} disabled={runPayroll.isPending}>
            {t('payroll:actions.runPayroll')}
          </Button>
        </div>
      ) : null}

      <RunPayrollConfirm
        open={confirmOpen}
        totalGrossPay={totalGrossPay}
        employeeCount={employeeCount}
        onConfirm={onConfirmRun}
        onCancel={() => setConfirmOpen(false)}
        confirmPending={runPayroll.isPending}
      />
    </div>
  );
}

function SummaryCard({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-md border border-border bg-card p-6">
      <p className="text-muted-foreground text-sm">{label}</p>
      <p
        className={
          mono
            ? 'mt-1 font-display font-mono font-semibold text-2xl text-foreground'
            : 'mt-1 font-display font-semibold text-2xl text-foreground'
        }
      >
        {value}
      </p>
    </div>
  );
}
